/**
 * The in-app guide recorder — the product team's way to make a guide, Tango-style,
 * with no developer tagging (decision G19).
 *
 * Three surfaces, all driven by `recorder-store.ts`:
 *
 * 1. **Before recording**, a confirmation the author must tick: they are about to
 *    record **live data**, and every step takes a screenshot (G20).
 * 2. **While recording**, an unmissable banner that stays across in-app navigation,
 *    with Stop and Discard. Each click, fill or selection outside GMS's own chrome
 *    becomes a step: a recorded locator (never the value typed) and a screenshot with
 *    the element outlined.
 * 3. **After Stop**, name the guide and upload it as drafts, then go straight to its
 *    Review & Edit page to blur anything personal and tidy the wording.
 *
 * Every screenshot is the page as it was when the author acted (G22). A fill or select
 * step is captured once, on its change, with the field put back in the copy to how it
 * looked before — empty, or its previous choice — so no picture shows what was typed.
 * Focusing a field copies nothing; it only notes a list's previous choice.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { guideReviewPath } from '../guide-screens';
import { useRecordGuide } from '../hooks/use-gms';
import { captureStep } from '../recorder/capture';
import {
  GMS_UI_ATTR,
  buildLocator,
  clickableFor,
  fieldAction,
  isGmsUi,
  hasFinder,
  isListItem,
  suggestStepText,
} from '../recorder/locator';
import {
  addRecordedStep,
  noteUnrecordable,
  discardRecording,
  getRecorderState,
  restoreRecording,
  resumeRecording,
  startRecording,
  stopRecording,
  useRecorder,
} from '../recorder/recorder-store';
import { useRoutePattern } from '../recorder/use-route-pattern';

const gmsUi = { [GMS_UI_ATTR]: '' };

/** The live-data confirmation shown before recording starts. */
function ConfirmRecording({ onStart }: { onStart: () => void }) {
  const [understood, setUnderstood] = useState(false);
  return (
    <div className="wb-gms-recorder-dialog" role="dialog" aria-modal="true" aria-label="Record a new guide" {...gmsUi}>
      <div className="wb-gms-recorder-dialog__card">
        <h2 className="wb-gms-recorder-dialog__title">Record a new guide</h2>
        <div className="wb-gms-note wb-gms-note--caution" role="alert">
          You are recording live data. Every step takes a screenshot of what is on screen.
          Don&apos;t open records with personal details where you can avoid it; you must
          blur anything personal before submitting.
        </div>
        <p className="wb-gms-entry__meta">
          Click through the task as you normally would. What you type is never recorded —
          only which field you filled in.
        </p>
        <label className="wb-gms-recorder-dialog__check">
          <input
            type="checkbox"
            checked={understood}
            onChange={(event) => setUnderstood(event.target.checked)}
          />{' '}
          I understand I am recording live data and will blur anything personal.
        </label>
        <div className="wb-gms-recorder-dialog__actions">
          <button type="button" className="wb-gms-guide__button" onClick={discardRecording}>
            Cancel
          </button>
          <button
            type="button"
            className="wb-gms-guide__button wb-gms-guide__button--primary"
            disabled={!understood}
            onClick={onStart}
          >
            Start recording
          </button>
        </div>
      </div>
    </div>
  );
}

/** The fixed banner shown for the whole recording. */
function RecordingBanner({ stepCount, unrecordable }: { stepCount: number; unrecordable: number }) {
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  return (
    <div className="wb-gms-recorder-banner" role="status" {...gmsUi}>
      <span className="wb-gms-recorder-banner__text">
        <span className="wb-gms-recorder-banner__dot" aria-hidden="true">
          ●
        </span>{' '}
        Recording live data — screenshots include what&apos;s on screen. Avoid personal
        details; blur before submitting.
      </span>
      <span className="wb-gms-recorder-banner__count">
        {stepCount} step{stepCount === 1 ? '' : 's'}
        {unrecordable
          ? ` · ${unrecordable} click${unrecordable === 1 ? '' : 's'} not recorded (nothing on ${unrecordable === 1 ? 'it' : 'them'} to find again)`
          : ''}
      </span>
      {confirmDiscard ? (
        <>
          <span>Discard this recording?</span>
          <button type="button" onClick={discardRecording}>
            Discard
          </button>
          <button type="button" onClick={() => setConfirmDiscard(false)}>
            Keep recording
          </button>
        </>
      ) : (
        <>
          <button type="button" onClick={stopRecording} disabled={stepCount === 0}>
            Stop
          </button>
          <button type="button" onClick={() => setConfirmDiscard(true)}>
            Discard
          </button>
        </>
      )}
    </div>
  );
}

/** Name the guide and upload it. */
function FinishRecording() {
  const recorder = useRecorder();
  const navigate = useNavigate();
  const record = useRecordGuide();
  const [title, setTitle] = useState('');
  const [screenName, setScreenName] = useState(recorder.startScreenName);
  const [error, setError] = useState('');

  const upload = useCallback(async () => {
    setError('');
    const pictures = await Promise.all(recorder.steps.map((step) => step.screenshot));
    const screenshots: Record<string, File> = {};
    recorder.steps.forEach((step, index) => {
      const file = pictures[index];
      if (file) screenshots[step.key] = file;
    });
    record.mutate(
      {
        recording: {
          schema: 1,
          screen: { route: recorder.startRoute, name: screenName.trim() || recorder.startRoute },
          title: title.trim(),
          summary: '',
          steps: recorder.steps.map((step) => ({
            key: step.key,
            text: step.text,
            locator: step.locator,
          })),
        },
        screenshots,
      },
      {
        onSuccess: (result) => {
          discardRecording();
          navigate(guideReviewPath(result.tool_key, result.topic_key));
        },
        onError: (failure) =>
          setError(failure instanceof Error ? failure.message : 'The upload failed.'),
      }
    );
  }, [recorder, record, screenName, title, navigate]);

  return (
    <div className="wb-gms-recorder-dialog" role="dialog" aria-modal="true" aria-label="Save the recording" {...gmsUi}>
      <div className="wb-gms-recorder-dialog__card">
        <h2 className="wb-gms-recorder-dialog__title">Save the recording</h2>
        <p className="wb-gms-entry__meta">
          {recorder.steps.length} step{recorder.steps.length === 1 ? '' : 's'} recorded. It
          is saved as a draft — nobody sees it until it is reviewed, blurred where needed,
          approved and published.
        </p>
        <label className="wb-gms-review__field">
          <span>Guide title</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="e.g. Add a guest to a BPM"
            maxLength={200}
          />
        </label>
        <label className="wb-gms-review__field">
          <span>Screen name</span>
          <input
            value={screenName}
            onChange={(event) => setScreenName(event.target.value)}
            maxLength={160}
          />
        </label>
        <ol className="wb-gms-recorder-dialog__steps">
          {recorder.steps.map((step) => (
            <li key={step.key}>{step.text}</li>
          ))}
        </ol>
        {error ? (
          <div className="wb-gms-note wb-gms-note--caution" role="alert">
            {error}
          </div>
        ) : null}
        <div className="wb-gms-recorder-dialog__actions">
          <button type="button" className="wb-gms-guide__button" onClick={resumeRecording}>
            Keep recording
          </button>
          <button type="button" className="wb-gms-guide__button" onClick={discardRecording}>
            Discard
          </button>
          <button
            type="button"
            className="wb-gms-guide__button wb-gms-guide__button--primary"
            disabled={!title.trim() || record.isPending}
            onClick={() => void upload()}
          >
            {record.isPending ? 'Saving…' : 'Save as draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Listens to the page while recording and turns each action into a step. Every
 * listener is on the capture phase, so the step is noted before the page reacts.
 */
function useRecordingListeners(active: boolean, routePattern: string) {
  const route = useRef(routePattern);
  route.current = routePattern;

  useEffect(() => {
    if (!active) return undefined;
    // A list's choice when it gained focus, so its picture can show the choice before.
    const choiceBefore = new WeakMap<Element, number>();
    let lastField: Element | null = null;

    const nextKey = () => `step-${getRecorderState().steps.length + 1}`;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target || isGmsUi(target)) return;
      const element = clickableFor(target);
      if (!element) return;
      const locator = buildLocator(element, route.current, 'click');
      lastField = null;
      if (!hasFinder(locator)) {
        // Nothing stable on it to find it again by; recording it would fail the upload.
        noteUnrecordable();
        return;
      }
      addRecordedStep({
        text: suggestStepText(locator, isListItem(element)),
        locator,
        screenshot: captureStep(element, nextKey()),
      });
    };

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Element | null;
      if (!target || isGmsUi(target)) return;
      if (target instanceof HTMLSelectElement && !choiceBefore.has(target)) {
        choiceBefore.set(target, target.selectedIndex);
      }
    };

    const onChange = (event: Event) => {
      const target = event.target as Element | null;
      if (!target || isGmsUi(target)) return;
      const action = fieldAction(target);
      if (!action) return;
      // Typing then tabbing away fires once; changing the same field again does not
      // need a second step.
      if (lastField === target) return;
      lastField = target;
      const locator = buildLocator(target, route.current, action);
      if (!hasFinder(locator)) {
        noteUnrecordable();
        return;
      }
      addRecordedStep({
        text: suggestStepText(locator),
        locator,
        screenshot: captureStep(
          target,
          nextKey(),
          action === 'fill'
            ? { blank: true }
            : { selectedIndex: choiceBefore.get(target) ?? -1 }
        ),
      });
      choiceBefore.delete(target);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('change', onChange, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('change', onChange, true);
    };
  }, [active]);
}

interface GuideRecorderProps {
  /** The current screen's name, when the server knows it. */
  screenName?: string;
}

/** Mounted by the header Guide button for authors; renders whatever stage is active. */
export function GuideRecorder({ screenName }: GuideRecorderProps) {
  // A recording interrupted by a reload comes back here — only for authors, who are
  // the only people this is mounted for.
  useEffect(() => restoreRecording(), []);
  const recorder = useRecorder();
  const routePattern = useRoutePattern();
  useRecordingListeners(recorder.status === 'recording', routePattern);

  if (recorder.status === 'idle' || typeof document === 'undefined') return null;

  return createPortal(
    <>
      {recorder.status === 'confirming' ? (
        <ConfirmRecording
          onStart={() =>
            startRecording(routePattern, screenName || document.title || routePattern)
          }
        />
      ) : null}
      {recorder.status === 'recording' ? (
        <RecordingBanner stepCount={recorder.steps.length} unrecordable={recorder.unrecordable} />
      ) : null}
      {recorder.status === 'finishing' ? <FinishRecording /> : null}
    </>,
    document.body
  );
}
